import React, { useEffect, useState } from "react";
import { Row, Col, Card, Typography, Spin, Badge, Tabs, Empty, Avatar, Button, message } from "antd";
import DealCardSkeleton from "./skeletons/DealCardSkeleton";
import PublicServ from "../service/PublicServ";
import { useNavigate } from "react-router-dom";
import SafeImage from "./SafeImage";
import { IconClock, IconShoppingCart, IconPlus, IconMinus } from "@tabler/icons-react";
import { getTimeLeft } from "../util/dateUtils";
import { CartContext } from "../CartContext";
import AuthServ from "../service/AuthServ";
import ProductCard from "./ProductCard";
import { LocationContext } from "../LocationContext";
import { useCategory } from '../context/CategoryContext';
import { useDeliveryEstimateContext } from "../context/DeliveryEstimateContext";
import { usePreference } from "../context/PreferenceContext";
import { useGlobalAuthModal } from "../util/GlobalAuthModal";
import { renderSafeText } from "../util/unitFormatter";
import "../css/DealsSection.css";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const DealsSection = ({ type = "today", title = "Today's Hot Deals", icon = "🔥", category = null, search = "", filters = {} }) => {
    const { t } = usePreference();
    const { cart, addToCart, updateQuantity, removeFromCart } = React.useContext(CartContext);
    const { showAlert } = useGlobalAuthModal();
    const navigate = useNavigate();
    const deliveryContext = useDeliveryEstimateContext();
    const registerShop = deliveryContext?.registerShop;
    const formatETA = deliveryContext?.formatETA;
    const [selectedCategory, setSelectedCategory] = useState(category || "All");
    const { parentCategories: categories } = useCategory();
    const [deals, setDeals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expanded, setExpanded] = useState(false);
    const { latitude, longitude, radius } = React.useContext(LocationContext);

    // Register shops from deals only if location is available
    useEffect(() => {
        if (!latitude || !longitude) return;
        deals.forEach(deal => {
            if (deal.product) {
                const shopId = deal.product.shopId?._id || deal.product.shopId;
                if (shopId && registerShop) registerShop(shopId);
            }
        });
    }, [deals, registerShop, latitude, longitude]);

    useEffect(() => {
        if (category) {
            setSelectedCategory(category);
        }
    }, [category]);

    useEffect(() => {
        setExpanded(false);
        fetchDeals();
    }, [selectedCategory, type, search, JSON.stringify(filters), latitude, longitude, radius]);

    const fetchDeals = async () => {
        setLoading(true);
        try {
            const params = { type };
            if (selectedCategory !== "All") {
                params.category = selectedCategory;
            }

            // Priority to filters.search, then prop search
            const activeSearch = filters.search || search;
            if (activeSearch) {
                params.search = activeSearch;
            }

            if (latitude && longitude) {
                params.lat = latitude;
                params.lng = longitude;
                params.radius = radius;
            }

            // Add Category, Brand and Rating Filters
            if (filters.categories && filters.categories.length > 0) {
                params.categories = filters.categories.join(',');
            }
            if (filters.brands && filters.brands.length > 0) {
                params.brands = filters.brands.join(',');
            }
            if (filters.rating) {
                params.rating = filters.rating;
            }

            const res = await PublicServ.getTodayDeals(params);
            const rawDeals = res.data || [];

            // Deduplicate: Keep best deal per product (merges identical products with different IDs)
            const uniqueDealsMap = new Map();
            rawDeals
                .filter(deal => deal.product && deal.product.isActive !== false)
                .forEach(deal => {
                    if (deal.product && deal.product.name) {
                        const productKey = renderSafeText(deal.product.name).toLowerCase();
                        const existing = uniqueDealsMap.get(productKey);
                        const currentPrice = parseFloat(deal.dealPrice);
                        const existingPrice = existing ? parseFloat(existing.dealPrice) : Infinity;

                        if (!existing || currentPrice < existingPrice) {
                            uniqueDealsMap.set(productKey, deal);
                        }
                    }
                });

            setDeals(Array.from(uniqueDealsMap.values()));
        } catch (err) {
            console.error(`Error fetching ${type} deals:`, err);
        } finally {
            setLoading(false);
        }
    };

    const handleAddToCart = async (e, product) => {
        e.stopPropagation();
        
        await addToCart(product._id, 1, {}, {
            name: product.name,
            image: product.imageUrl || (product.images && product.images[0]),
            price: product.price,
            mrp: product.mrp,
            shopId: product.shopId?._id || product.shopId
        });
    };

    const handleIncrease = async (e, productId, qty) => {
        e.stopPropagation();
        const cartItem = cart.items.find(item => (item.product?._id || item.product) === productId);
        if (cartItem?._id) {
            await updateQuantity(cartItem._id, qty + 1);
        }
    };

    const handleDecrease = async (e, productId, qty) => {
        e.stopPropagation();
        const cartItem = cart.items.find(item => (item.product?._id || item.product) === productId);
        if (cartItem?._id) {
            if (qty > 1) {
                await updateQuantity(cartItem._id, qty - 1);
            } else {
                await removeFromCart(cartItem._id);
            }
        }
    };

    // Extract unique shops from deals
    const getRelatedShops = () => {
        const shopsMap = new Map();
        deals.forEach(deal => {
            if (deal.product && deal.product.shopId) {
                const shop = deal.product.shopId;
                if (!shopsMap.has(shop._id)) {
                    shopsMap.set(shop._id, shop);
                }
            }
        });
        return Array.from(shopsMap.values());
    };

    const relatedShops = getRelatedShops();

    if (!loading && deals.length === 0 && (selectedCategory === "All" || category)) return null;

    return (
        <div className="deals-section-container">
            <div className="deals-header" >
                <div className="deals-header-content">
                    <span>{icon} {title}</span>
                </div>
            </div>

            {loading ? (
                <Row gutter={[10, 10]}>
                    {[...Array(8)].map((_, i) => (
                        <Col xs={12} sm={8} md={6} lg={4} xl={3} key={i}>
                            <DealCardSkeleton />
                        </Col>
                    ))}
                </Row>
            ) : (
                <>
                    {deals.length > 0 ? (
                        <>
                            <Row gutter={[10, 10]}>
                                {deals.slice(0, expanded ? deals.length : 6).map((deal) => {
                                    const originalPrice = deal.product?.mrp || deal.product?.price || 0;
                                    const discountPercent = originalPrice > deal.dealPrice
                                        ? Math.round(((originalPrice - deal.dealPrice) / originalPrice) * 100)
                                        : deal.discountPercentage;

                                    return (
                                        <Col xs={12} sm={8} md={6} lg={4} xl={3} key={deal._id}>
                                            <ProductCard product={deal.product} isHotDeal={true} formatETA={formatETA} />
                                        </Col>
                                    );
                                })}
                            </Row>
                            {deals.length > 6 && (
                                <div className="deals-more-btn-container">
                                    <Button
                                        type="primary"
                                        onClick={() => setExpanded(!expanded)}
                                        shape="round"
                                        size="large"
                                    >
                                        {expanded ? t('less') : t('more')}
                                    </Button>
                                </div>
                            )}
                        </>
                    ) : (

                        <Empty description={t('no_record_found')} className="pcu-padding-40" />
                    )}
                </>
            )}
        </div>
    );
};

export default DealsSection;
